// Código de descuento junto al equipo (slot `discountCode`, REQ-14): uno por venta y solo antes del cobro.
import { useEffect, useState } from 'react';
import { QrCode } from 'lucide-react';
import { Button, Input } from '@/kit';
import type { DiscountCodeSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { useApplyPromotions } from '../hooks/useApplyPromotions';
import { useDiscountCodeScanner } from '../hooks/useDiscountCodeScanner';
import { normalizeDiscountCode } from '../lib/discountCodeText';
import { discountCodeBlockedCopy, droppedCodeCopy } from '../lib/promotionErrorCopy';
import { useSessionPromotionState } from '../store/promotionSelectionStore';

export interface DiscountCodeFieldProps extends DiscountCodeSlotContext {
    sessionId: string;
}

export function DiscountCodeField({ sessionId, close, ...context }: DiscountCodeFieldProps) {
    const { anchorLine, quoteState, confirmQuoteDiscard } = context;
    const state = useSessionPromotionState(sessionId);
    const { apply, isApplying, error, resetError } = useApplyPromotions({ sessionId, context });
    const scanner = useDiscountCodeScanner();
    const [text, setText] = useState('');
    const [emptyError, setEmptyError] = useState(false);
    const applied = state.synced ? state.selection.code : (anchorLine.discountCode ?? null);

    // El error de un intento anterior no sobrevive a otra venta ni a otro código aplicado.
    const saleKey = [
        state.linesVersion,
        anchorLine.lineId,
        anchorLine.sku,
        context.anchorGiftLines.length,
        applied ?? '',
    ].join('|');
    useEffect(() => {
        resetError();
    }, [saleKey, resetError]);

    // Cobrada, o el servidor bloqueó el código: se pliega con su motivo.
    const blockedBy =
        quoteState === 'charged' ? 'DISCOUNT_AFTER_QUOTE' : state.discountCodeBlockedBy;
    if (blockedBy) {
        return (
            <p role="status" className="text-[12px] text-muted-foreground">
                {discountCodeBlockedCopy(blockedBy)}
            </p>
        );
    }

    const run = (discountCode: string | null): void => {
        confirmQuoteDiscard()
            .then(async (ok) => {
                if (!ok) return;
                await apply((base) => ({ ...base, code: discountCode }));
                setText('');
                // Sin código se vuelve a plegar.
                if (discountCode === null) close();
            })
            .catch(() => undefined);
    };

    if (applied) {
        return (
            <div className="flex flex-col gap-1.5 text-[13px]">
                <div className="flex items-center justify-between gap-2">
                    <span>
                        Código <strong>{applied}</strong> aplicado
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        isLoading={isApplying}
                        onClick={() => {
                            run(null);
                        }}
                    >
                        Quitar
                    </Button>
                </div>
                {error && (
                    <p role="alert" className="text-[12px] text-destructive">
                        {error}
                    </p>
                )}
            </div>
        );
    }

    const submit = (): void => {
        const code = normalizeDiscountCode(text);
        setEmptyError(code === '');
        if (code) run(code);
    };

    return (
        <form
            className="flex flex-col gap-1.5"
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
        >
            <div className="flex items-start gap-2">
                <Input
                    aria-label="Código de descuento"
                    placeholder="Escribe el código del cliente"
                    value={text}
                    autoCapitalize="characters"
                    onChange={(e) => {
                        setText(e.target.value);
                        setEmptyError(false);
                    }}
                />
                {scanner.available && (
                    <Button type="button" variant="outline" aria-label="Escanear QR">
                        <QrCode className="size-4" aria-hidden />
                    </Button>
                )}
                <Button type="submit" isLoading={isApplying}>
                    Aplicar
                </Button>
            </div>
            {(emptyError || error) && (
                <p role="alert" className="text-[12px] text-destructive">
                    {emptyError ? 'Escribe un código.' : error}
                </p>
            )}
            {!emptyError && !error && state.droppedCode && (
                <p role="status" className="text-[12px] text-warning">
                    {droppedCodeCopy(state.droppedCode)}
                </p>
            )}
        </form>
    );
}
